import {
	Box,
	CardContent,
	CardMedia,
	Chip,
	IconButton,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	tooltipClasses,
	useTheme,
} from '@mui/material';
import React from 'react';

import {AlertInfo} from 'presentation/components/AlertInfo';
import {AlertSuspendedOrCanceled} from 'presentation/components/AlertSuspendedOrCanceled';
import Icon from 'presentation/icons';
import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {translations} from 'presentation/translations-new';
import {formatText, getCardBackgroundImage} from 'presentation/utils';
import {ProfileMenuEnum} from '../../../../../../../tests/constants/ProfileMenuEnum';
import {BeneficiaryInfoProps} from './types';

const {responsibleWithoutBenefits} = translations['pt-br'].commons;
const {walletAltText, socialName} = translations['pt-br'].others.profileMenu;

export function BeneficiaryInfoLegacy({
	hasSocialNameToolTip,
	isResponsibleWithoutBenefits,
}: BeneficiaryInfoProps) {
	const {palette} = useTheme();
	const {isMobile, isDesktop} = useMediaQueries();
	const {data: healthCardData, isError, isLoading} = useGetHealthCardQuery();
	const {beneficiary} = useAccountInformation();

	if (isLoading) {
		return <Skeleton variant="rectangular" height={45} />;
	}

	if (isError || !healthCardData) {
		return null;
	}

	const {flPlanoIntegrado, cdModeloCarteira} = healthCardData;

	const background = getCardBackgroundImage({
		planType: cdModeloCarteira,
		isIntegrated: flPlanoIntegrado,
	});

	return (
		<Box width="100%">
			{isResponsibleWithoutBenefits ? (
				<AlertInfo.Root
					severity="info"
					sx={{
						whiteSpace: 'normal',
						borderRadius: 0,
					}}
					data-testid={ProfileMenuEnum.FINANCIAL_RESPONSIBLE}>
					<AlertInfo.Description>{responsibleWithoutBenefits}</AlertInfo.Description>
				</AlertInfo.Root>
			) : (
				<CardMedia sx={{height: 45}} image={background} title={walletAltText} />
			)}

			<CardContent
				sx={{
					paddingTop: 0,
					px: isMobile || isDesktop ? 2 : 4,
					mt: 2,
				}}>
				<Stack alignItems="center">
					<Typography
						variant="h6"
						color="primary"
						textAlign="center"
						whiteSpace="pre-wrap"
						sx={{paddingBottom: isMobile ? '8px' : 0}}>
						{formatText(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC)}
						{hasSocialNameToolTip ? (
							<Tooltip
								enterTouchDelay={0}
								slotProps={{
									popper: {
										sx: {
											[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
												{
													marginTop: '0px',
													backgroundColor:
														palette.other.snackbarBackground,
													padding: '16px 12px',
													fontSize: '1rem',
												},
											[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]:
												{
													color: palette.other.snackbarBackground,
												},
										},
									},
								}}
								placement="bottom-end"
								arrow
								title={socialName}
								data-testid={ProfileMenuEnum.SOCIAL_NAME}>
								<IconButton sx={{mb: '2px'}}>
									<Icon
										name="info-filled"
										size={24}
										color={palette.primary.main}
									/>
								</IconButton>
							</Tooltip>
						) : null}
					</Typography>
					<Chip
						size="small"
						data-testid={beneficiary?.tipoUsuarioC}
						label={formatText(beneficiary?.tipoUsuarioC)}
					/>
				</Stack>
			</CardContent>
			<AlertSuspendedOrCanceled />
		</Box>
	);
}
